import { html, LitElement, nothing, unsafeCSS } from 'lit'
import { customElement, property, query, queryAssignedElements, state } from 'lit/decorators.js'
import { ifDefined } from 'lit/directives/if-defined.js'
import { createRef, ref } from 'lit/directives/ref.js'
import { when } from 'lit/directives/when.js'
import { distinctUntilChanged, filter, fromEvent, map, takeUntil, timer } from 'rxjs'
import { otp } from '@schmancy/directives/otp'

import { HostAriaLabelMixin, hostIsTheControl, SchmancyFormField } from '@schmancy/mixins/index'
import { CONTROL_SIZE, controlSizeStyles } from '@schmancy/theme/control-size'

// Side-effect imports: both tags are rendered into this shadow root, and a
// type-only import of a component is elided by the compiler, so the element
// would never register and would paint as an unupgraded unknown tag.
import '@schmancy/primitives/button/icon-button'
import '@schmancy/primitives/icons/icon'

import { FieldActionsController } from '../field-actions'
import { fieldChromeClasses } from './input-chrome'
import style from './input.scss?inline'

// If you want to be form-associated, define the type on `ElementInternals`.
declare global {
	interface HTMLElementTagNameMap {
		'schmancy-input': SchmancyInput
		'sch-input': SchmancyInput
	}
}

type EventDetails = {
	value: string
}

export type SchmancyInputInputEvent = CustomEvent<EventDetails>
export type SchmancyInputChangeEvent = CustomEvent<EventDetails>
export type SchmancyInputEnterEvent = CustomEvent<EventDetails>

/**
 * Size variants for the input - M3 spec aligned.
 * - xxs: Ultra-compact (24dp) - for menu cards, compact UIs
 * - xs: Dense/compact (32dp) - M3 density -3
 * - sm: Default (40dp) - M3 density 0
 * - md: Standard (48dp) - M3 large
 * - lg: Extra large (56dp) - M3 extra large
 */
export type InputSize = 'xxs' | 'xs' | 'sm' | 'md' | 'lg'

function otpDigits(value: string, maxlength?: number): string {
	return value.replace(/[^0-9]/g, '').slice(0, maxlength === undefined ? 6 : maxlength < 0 ? undefined : maxlength)
}

/**
 * The clear control's rung, one below the field's own so the button sits inside
 * the field's box with the field's own padding clear on either side of it.
 * Spelled as a literal map rather than derived by index arithmetic, because a
 * five-rung ladder has no arithmetic worth hiding and `xxs` has nowhere to fall.
 */
const CLEAR_CONTROL_SIZE: Record<InputSize, InputSize> = {
	xxs: 'xxs',
	xs: 'xxs',
	sm: 'xs',
	md: 'xs',
	lg: 'sm',
}

/**
 * @slot prefix - Decorative adornment pinned inside the field's leading edge (a
 *   search glyph, a currency mark). Click-through by design — a tap on it lands
 *   on the field and focuses it — so it holds display content, never a control.
 * @slot suffix - The same, pinned inside the trailing edge. It yields the edge
 *   to the field's own trailing furniture — the `clearable` control, the
 *   `picker` chevron — and moves one glyph inboard when either is present, so
 *   the two never share a pixel.
 * @slot actions - A CONTROL that acts on this field's value, rendered inside the
 *   field's own chrome at the trailing edge — the third role beside `prefix` and
 *   `suffix`, which are click-through decoration a control could never live in.
 *   It takes the outermost trailing position and everything else in that band —
 *   an authored `suffix`, the `clearable` control, the `picker` chevron — steps
 *   inboard of it, and the field's own text padding grows by its width so a
 *   click near the text can never land on it. It follows the field in tab order.
 *   On a field narrower than `FIELD_ACTIONS_COMPACT_WIDTH` the field sets
 *   `compact` on whatever is in the slot; `<schmancy-button compact>` renders
 *   icon-only and keeps its label as its accessible name.
 * @csspart picker - The `<button>` a `picker` field renders in place of the
 *   `<input>`.
 * @csspart actions - The box holding the `actions` slot.
 */
@customElement('schmancy-input')
export default class SchmancyInput extends HostAriaLabelMixin(SchmancyFormField(unsafeCSS(style))) {
	private inputValue = ''

	@property({ type: String, reflect: true })
	public override get value(): string {
		return this.inputValue
	}

	public override set value(value: string) {
		const previous = this.inputValue
		this.inputValue = this.otp ? otpDigits(value ?? '', this.maxlength) : (value ?? '')
		this.requestUpdate('value', previous)
	}

	/** One native text field for a numeric one-time code. The value contains only ASCII digits. */
	@property({ type: Boolean, reflect: true }) public otp = false
	@property({ type: String, attribute: 'otp-alignment', reflect: true }) public otpAlignment: 'left' | 'center' = 'center'

	@property({ reflect: true })
	public type: HTMLInputElement['type'] = 'text'

	@property()
	public placeholder = ''

	@property({ type: String, reflect: true })
	public pattern?: string

	/** Visually shows a pointer cursor even while readonly. */
	@property({ type: Boolean, reflect: true }) public clickable = false

	/**
	 * Renders an internal clear control — a real, named, tab-reachable button —
	 * for as long as `value` is non-empty. Activating it empties the field,
	 * dispatches the same `input` + `change` pair a user edit does, returns
	 * focus to the field, and re-runs validity, so a required field cleared
	 * this way reports `valueMissing` exactly as one emptied by keyboard.
	 *
	 * Never offered on a `disabled` or `readonly` field, which take no input at
	 * all, nor on a `picker` field, which is itself a button and inside which no
	 * second control may nest.
	 */
	@property({ type: Boolean, reflect: true }) public clearable = false

	/**
	 * Replaces the clear control's whole accessible name. Left unset the name is
	 * `Clear <label>` where the field carries a label and `Clear` where it does
	 * not — replaced whole rather than composed from a translated verb, because
	 * `${verb} ${label}` inverts in most of the languages this library ships to.
	 */
	@property({ type: String, attribute: 'clear-label' }) public clearLabel = ''

	/**
	 * The field is pressed to open a picker rather than typed into. It renders a
	 * real `<button>` wearing this field's own chrome, label, hint and error
	 * treatment, with a trailing chevron marking that pressing it opens
	 * something; the consumer binds `@click` and opens whatever that is, then
	 * writes the result back to `value`.
	 *
	 * It stays a form field throughout — `value` is what the form collects,
	 * `required` still reports `valueMissing`, and reset still restores the
	 * first-rendered value — which is the whole reason this is a mode of the
	 * field rather than a row primitive of its own.
	 */
	@property({ type: Boolean, reflect: true }) public picker = false

	@property({ type: Boolean, reflect: true })
	public spellcheck = false

	@property({ type: String, reflect: true })
	public align: 'left' | 'center' | 'right' = 'left'

	@property()
	public inputmode?: 'none' | 'text' | 'decimal' | 'numeric' | 'tel' | 'search' | 'email' | 'url'

	@property({ type: Number, reflect: true })
	public minlength?: number

	@property({ type: Number })
	public maxlength?: number

	@property()
	public min?: string

	@property()
	public max?: string

	@property({ reflect: true })
	public step?: string = 'any'

	@property({ type: Boolean })
	public autofocus = false

	@property({ type: String, reflect: true })
	public autocomplete: AutoFill = 'on'

	@property({ type: Number, reflect: true })
	public override tabIndex = 0

	@property({ type: String, reflect: true })
	public size: InputSize = 'md'

	/**
	 * `filled` is a quiet borderless well that recedes into any ground — a search field in a toolbar or header.
	 * `bare` has no box at all until it is hovered or focused: a filter in a tool's row.
	 */
	@property({ reflect: true })
	public variant: 'outlined' | 'filled' | 'bare' = 'outlined'

	/**
	 * `compact` sizes a text field for a tool's row, in place of `size`: 24px at 11px where the nearest
	 * container is 36rem or wider, and 32px at 12px, a finger's, where it is narrower or there is none.
	 */
	@property({ reflect: true })
	public density?: 'compact'

	@property({ type: String })
	public list?: string

	@query('input') private inputElement!: HTMLInputElement
	private inputRef = createRef<HTMLInputElement>()
	private pickerRef = createRef<HTMLButtonElement>()

	@state()
	private isAutofilled = false

	@queryAssignedElements({ slot: 'prefix', flatten: true })
	private prefixAdornments!: HTMLElement[]

	@queryAssignedElements({ slot: 'suffix', flatten: true })
	private suffixAdornments!: HTMLElement[]

	@state()
	private hasPrefix = false

	@state()
	private hasSuffix = false

	/** Owns the `actions` slot's shared half — see `../field-actions`. */
	private readonly fieldActions = new FieldActionsController(this)

	// `touched`, `dirty`, `submitted`, and `validateOn` come from FormFieldMixin.
	// `defaultValue` (mixin) replaces the old `defaultValue` field.

	// ----------------------------
	//  D) Form-associated logic
	// ----------------------------
	protected static shadowRootOptions = {
		...LitElement.shadowRootOptions,
		delegatesFocus: true, // so focus() goes to <input>
	}

	protected override willUpdate(changedProps: Map<PropertyKey, unknown>) {
		if (this.otp && (changedProps.has('otp') || changedProps.has('maxlength'))) {
			const digits = otpDigits(this.value, this.maxlength)
			if (digits !== this.value) this.value = digits
		}
		super.willUpdate(changedProps as never)
	}

	// `updated()` removed — FormFieldMixin's `willUpdate` recomputes `dirty`,
	// runs `checkValidity()` on every value change, and updates `:state(dirty)`
	// automatically. `shouldShowError()` gates only whether that verdict is
	// painted, inside `setFieldValidity`, never whether it runs.
	//
	// Default value capture is also handled by the mixin (`firstUpdated` sets
	// `defaultValue` from `value`).

	connectedCallback() {
		super.connectedCallback()

		// Form reset and submit are now handled by the mixin via FACE callbacks
		// (`formResetCallback`) and by `<schmancy-form>`'s submit sweep
		// (`markSubmitted()`). No manual form listeners needed.
		this.setupExternalLabelAssociation()

		// Re-wire on RECONNECT: firstUpdated ran once and the per-connection
		// `disconnecting` tore down the prior connection's subs on the move-out;
		// `hasUpdated` is false before the first render (firstUpdated owns that),
		// true on every reparent (schmancy-menu moves slotted content into its
		// overlay on open).
		if (this.hasUpdated) this.wireInputElementEvents()
	}

	/**
	 * Set up external label association for native HTML label support
	 */
	private setupExternalLabelAssociation() {
		if (this.id) {
			const setupLabelClickListener = () => {
				const labels = document.querySelectorAll(`label[for="${this.id}"]`)
				labels.forEach(label => {
					fromEvent(label, 'click')
						.pipe(takeUntil(this.disconnecting))
						.subscribe(() => {
							this.focus()
						})
				})
			}

			if (document.readyState === 'complete') {
				setupLabelClickListener()
			} else {
				fromEvent(document, 'DOMContentLoaded').pipe(takeUntil(this.disconnecting)).subscribe(setupLabelClickListener)
			}
		}
	}

	// `disconnectedCallback`, `resetToDefault`, `shouldShowValidation`, and
	// `validateInput` are gone — the mixin's `willUpdate` runs `checkValidity()`
	// at the right moments (Phase 2/3/4 of the validation contract) and
	// `formResetCallback` → `resetForm()` handles reset.

	/** Re-runs checkValidity after render, once the native input's `.value` binding has propagated — the mixin's willUpdate fires too early, while native.validity still reflects the previous value. */
	protected override updated(changedProps: Map<PropertyKey, unknown>) {
		super.updated?.(changedProps)
		if (
			changedProps.has('value') ||
			changedProps.has('required') ||
			changedProps.has('type') ||
			changedProps.has('pattern') ||
			changedProps.has('min') ||
			changedProps.has('max') ||
			changedProps.has('minlength') ||
			changedProps.has('maxlength')
		) {
			this.checkValidity()
		}
	}

	/**
	 * Pick the first matching `errorMessages` override for the failing flags.
	 * Order matches the natural priority of validity flags (valueMissing
	 * before more specific ones — empty trumps malformed).
	 */
	private firstMatchingErrorMessage(v: ValidityState): string | undefined {
		const m = this.errorMessages
		if (!m) return undefined
		if (v.valueMissing && m.valueMissing) return m.valueMissing
		if (v.typeMismatch && m.typeMismatch) return m.typeMismatch
		if (v.patternMismatch && m.patternMismatch) return m.patternMismatch
		if (v.tooShort && m.tooShort) return m.tooShort
		if (v.tooLong && m.tooLong) return m.tooLong
		if (v.rangeUnderflow && m.rangeUnderflow) return m.rangeUnderflow
		if (v.rangeOverflow && m.rangeOverflow) return m.rangeOverflow
		if (v.stepMismatch && m.stepMismatch) return m.stepMismatch
		if (v.badInput && m.badInput) return m.badInput
		return undefined
	}

	/** Folds the native `<input>`'s ValidityState flags into `internals.setValidity` so consumers can target `field.validity.<flag>` and `:state(value-missing|type-mismatch|…)`. */
	public override checkValidity() {
		// @query is always live; @ref may lag during initial paint.
		const native = this.inputElement ?? this.inputRef.value
		// Self-heal a stale native `customError`: WebKit's form-associated
		// custom element + iOS autofill interaction is documented to leave a
		// native `<input>`'s own `customError` flag set independent of anything
		// this component actually passed to `setCustomValidity` — folding it
		// into `internals.setValidity` below would surface as a permanently
		// stuck "Invalid value" message, since the mixin's `checkValidity()`
		// only ever preserves a custom error it tracked itself
		// (`hasExplicitCustomError()`), never one read back from a validity
		// object this component doesn't exclusively own. Only OUR OWN explicit
		// `setCustomValidity` call may leave the native flag set; anything else
		// observed here is platform drift, cleared every pass.
		if (native?.validity.customError && !this.hasExplicitCustomError()) {
			native.setCustomValidity('')
		}
		const v = native?.validity
		if (v && !v.valid) {
			// Surface the native flags. valueMissing also surfaces here when
			// `required` + empty — overrides the mixin's coarse check.
			const flags: ValidityStateFlags = {
				valueMissing: v.valueMissing || undefined,
				typeMismatch: v.typeMismatch || undefined,
				patternMismatch: v.patternMismatch || undefined,
				tooShort: v.tooShort || undefined,
				tooLong: v.tooLong || undefined,
				rangeUnderflow: v.rangeUnderflow || undefined,
				rangeOverflow: v.rangeOverflow || undefined,
				stepMismatch: v.stepMismatch || undefined,
				badInput: v.badInput || undefined,
				customError: v.customError || undefined,
			}
			this.setFieldValidity(flags, this.firstMatchingErrorMessage(v) || native.validationMessage || 'Invalid value')
			if (this.shouldShowError()) {
				for (const flag of [
					'value-missing',
					'type-mismatch',
					'pattern-mismatch',
					'too-short',
					'too-long',
					'range-underflow',
					'range-overflow',
					'step-mismatch',
					'bad-input',
				]) {
					this.internals?.states.delete(flag)
				}
				if (v.valueMissing) this.internals?.states.add('value-missing')
				if (v.typeMismatch) this.internals?.states.add('type-mismatch')
				if (v.patternMismatch) this.internals?.states.add('pattern-mismatch')
				if (v.tooShort) this.internals?.states.add('too-short')
				if (v.tooLong) this.internals?.states.add('too-long')
				if (v.rangeUnderflow) this.internals?.states.add('range-underflow')
				if (v.rangeOverflow) this.internals?.states.add('range-overflow')
				if (v.stepMismatch) this.internals?.states.add('step-mismatch')
				if (v.badInput) this.internals?.states.add('bad-input')
			}
			return false
		}

		// Native validity passing doesn't guarantee the mixin's own required
		// check passes too (native treats empty differently for type=email/url).
		const parentValid = super.checkValidity()
		if (parentValid) {
			for (const flag of [
				'value-missing',
				'type-mismatch',
				'pattern-mismatch',
				'too-short',
				'too-long',
				'range-underflow',
				'range-overflow',
				'step-mismatch',
				'bad-input',
			]) {
				this.internals?.states.delete(flag)
			}
		}
		return parentValid
	}

	/** Marks the field submitted so the mixin's error-visibility gate flips to "always show". */
	public override reportValidity() {
		this.markSubmitted()
		const inputValid = this.inputRef.value?.reportValidity() ?? true
		const parentValid = super.reportValidity()
		return inputValid && parentValid
	}

	public override setCustomValidity(message: string) {
		if (this.inputRef.value) {
			this.inputRef.value.setCustomValidity(message)
		}
		super.setCustomValidity(message)
	}

	protected override firstUpdated(changedProps: Map<PropertyKey, unknown>) {
		// The mixin's own firstUpdated is what captures `defaultValue` from the
		// first-rendered value. Without this call it never runs: `defaultValue`
		// stays undefined, so `dirty` is false for every value the user can ever
		// type, the default `validateOn: 'dirty'` gate never opens, and
		// `resetForm()` restores '' over whatever the field first rendered with.
		super.firstUpdated?.(changedProps as never)

		// firstUpdated only — never re-run on reconnect, or a menu-slotted input
		// would steal focus back every time the menu reopens.
		if (this.autofocus) {
			timer(0)
				.pipe(takeUntil(this.disconnecting))
				.subscribe(() => this.focus())
		}

		this.wireInputElementEvents()
	}

	/**
	 * Bound to both adornment slots' `slotchange`. The flags drive only the
	 * `<input>`'s inline padding — an empty slot must leave the field's box
	 * exactly as an un-slotted field's, so the padding is added, never reserved.
	 */
	private syncAdornments() {
		this.hasPrefix = this.prefixAdornments.length > 0
		this.hasSuffix = this.suffixAdornments.length > 0
	}

	/** Called from firstUpdated (shadow DOM ready) and again from connectedCallback on reconnect (see the `if (this.hasUpdated)` guard there). */
	private wireInputElementEvents() {
		// A `picker` field renders a <button> where the <input> would be, so
		// there is no element for these four to bind to; the button needs none
		// of them (activation is the native click a <button> already dispatches).
		if (!this.inputElement) return
		this.setupInputEvents()
		this.setupFocusBlurEvents()
		this.setupAutofillDetection()
		this.setupEnterKeyEvents()
	}

	private setupInputEvents() {
		fromEvent<InputEvent>(this.inputElement, 'input')
			.pipe(
				map(ev => {
					// Stop the native event — we dispatch our own custom event instead.
					ev.stopPropagation()

					const inputEvent = ev as InputEvent
					const target = ev.target as HTMLInputElement
					return {
						value: target.value,
						inputType: inputEvent.inputType,
						data: inputEvent.data,
						isComposing: inputEvent.isComposing,
						originalEvent: ev,
					}
				}),
				takeUntil(this.disconnecting),
			)
			.subscribe(eventData => {
				if (this.otp) {
					const native = this.inputElement
					const start = otpDigits(
						eventData.value.slice(0, native.selectionStart ?? eventData.value.length),
						this.maxlength,
					).length
					const end = otpDigits(
						eventData.value.slice(0, native.selectionEnd ?? eventData.value.length),
						this.maxlength,
					).length
					this.value = eventData.value
					if (native.value !== this.value) {
						native.value = this.value
						native.setSelectionRange(start, end, native.selectionDirection ?? 'none')
					}
				} else {
					this.value = eventData.value
				}

				// `dirty` is a getter on the mixin (value vs defaultValue) — no manual set.

				const customEvent = new CustomEvent<EventDetails>('input', {
					detail: { value: this.value },
					bubbles: true,
					composed: true,
				})

				Object.defineProperties(customEvent, {
					inputType: { value: eventData.inputType },
					data: { value: eventData.data },
					isComposing: { value: eventData.isComposing },
				})

				this.dispatchEvent(customEvent)

				// Mixin's willUpdate runs checkValidity() on every value-change, so
				// platform validity always tracks the value — no manual
				// validateInput() call. Whether that verdict reaches the screen is
				// decided inside setFieldValidity, behind shouldShowError().
			})

		fromEvent<Event>(this.inputElement, 'change')
			.pipe(
				map(ev => {
					ev.stopPropagation()
					return (ev.target as HTMLInputElement).value
				}),
				distinctUntilChanged(),
				takeUntil(this.disconnecting),
			)
			.subscribe(value => {
				this.value = value
				if (this.otp && this.inputElement.value !== this.value) this.inputElement.value = this.value
				this.emitChange({ value: this.value })
			})
	}

	private setupFocusBlurEvents() {
		fromEvent<FocusEvent>(this.inputElement, 'focus')
			.pipe(takeUntil(this.disconnecting))
			.subscribe(ev => {
				const focusEvent = new CustomEvent('focus', {
					bubbles: ev.bubbles,
					cancelable: ev.cancelable,
					composed: ev.composed,
				})

				Object.defineProperties(focusEvent, {
					relatedTarget: { value: ev.relatedTarget },
				})

				this.dispatchEvent(focusEvent)
			})

		fromEvent<FocusEvent>(this.inputElement, 'blur')
			.pipe(takeUntil(this.disconnecting))
			.subscribe(ev => {
				// mixin's willUpdate handles the rest (validates if dirty, broadcasts :state(touched)).
				this.markTouched()

				const blurEvent = new CustomEvent('blur', {
					bubbles: ev.bubbles,
					cancelable: ev.cancelable,
					composed: ev.composed,
				})

				Object.defineProperties(blurEvent, {
					relatedTarget: { value: ev.relatedTarget },
				})

				this.dispatchEvent(blurEvent)
			})
	}

	private setupAutofillDetection() {
		fromEvent<AnimationEvent>(this.inputElement, 'animationstart')
			.pipe(
				filter(ev => ev.animationName === 'onAutoFillStart'),
				takeUntil(this.disconnecting),
			)
			.subscribe(ev => {
				const native = ev.target as HTMLInputElement
				this.value = native.value
				if (this.otp) native.value = this.value
				this.isAutofilled = true
				// An autofilled value arrives whole, in one act, into a field the user
				// may never focus — so there is no blur coming to end an entry that
				// never happened, and an autofilled address the field refuses has to
				// say so now rather than never.
				this.markTouched()

				this.dispatchEvent(
					new CustomEvent('autofill', {
						detail: { value: this.value },
						bubbles: true,
						composed: true,
					}),
				)

				if (this.otp) {
					this.dispatchEvent(
						new CustomEvent<EventDetails>('input', { detail: { value: this.value }, bubbles: true, composed: true }),
					)
				}
				this.emitChange({ value: this.value })
			})

		fromEvent<AnimationEvent>(this.inputElement, 'animationstart')
			.pipe(
				filter(ev => ev.animationName === 'onAutoFillCancel'),
				takeUntil(this.disconnecting),
			)
			.subscribe(() => {
				this.isAutofilled = false
			})
	}

	private setupEnterKeyEvents() {
		fromEvent<KeyboardEvent>(this.inputElement, 'keydown')
			.pipe(
				filter(ev => ev.key === 'Enter'),
				takeUntil(this.disconnecting),
			)
			.subscribe(ev => {
				const { value } = ev.target as HTMLInputElement

				if (this.value !== value) {
					this.value = value
					if (this.otp) this.inputElement.value = this.value
				}

				// Blur to trigger change naturally, mimicking a tab-out.
				this.inputElement.blur()

				const enterEvent = new CustomEvent<EventDetails>('enter', {
					detail: { value: this.value },
					bubbles: true,
					composed: true,
				})

				Object.defineProperties(enterEvent, {
					key: { value: 'Enter' },
					code: { value: 'Enter' },
					keyCode: { value: 13 },
					which: { value: 13 },
				})

				this.dispatchEvent(enterEvent)
			})
	}

	/**
	 * Bound as a class field so the `@click` binding keeps one reference across
	 * renders (the carve-out `CALL_SITE_THRESHOLD_TWO` grants a Lit handler).
	 *
	 * The native `.value` is emptied before `this.value`, so the synchronous
	 * `checkValidity()` below reads the emptied control rather than the previous
	 * value's verdict — a consumer inspecting `internals.validity` from inside
	 * its own `change` handler sees `valueMissing` on a cleared required field
	 * in that same turn, not one frame later.
	 */
	private clearValue = () => {
		const native = this.inputRef.value
		if (native) native.value = ''
		this.value = ''
		this.markTouched()
		this.dispatchEvent(new CustomEvent<EventDetails>('input', { detail: { value: '' }, bubbles: true, composed: true }))
		this.emitChange({ value: '' })
		this.checkValidity()
		// Before the clear control leaves the tree on the next render, so focus
		// lands on the field rather than falling to the document body.
		this.focus()
	}

	public select() {
		return this.inputRef.value?.select()
	}

	public getValidity(): ValidityState | undefined {
		return this.inputRef.value?.validity
	}

	public setSelectionRange(start: number, end: number, direction?: 'forward' | 'backward' | 'none') {
		this.inputRef.value?.setSelectionRange(start, end, direction)
	}

	public get selectionStart(): number | null {
		return this.inputRef.value?.selectionStart ?? null
	}

	public get selectionEnd(): number | null {
		return this.inputRef.value?.selectionEnd ?? null
	}

	public get selectionDirection(): 'forward' | 'backward' | 'none' | null {
		return this.inputRef.value?.selectionDirection ?? null
	}

	public setRangeText(
		replacement: string,
		start?: number,
		end?: number,
		selectMode?: 'select' | 'start' | 'end' | 'preserve',
	) {
		if (start !== undefined && end !== undefined) {
			this.inputRef.value?.setRangeText(replacement, start, end, selectMode)
		} else {
			this.inputRef.value?.setRangeText(replacement)
		}
	}

	public override focus(options?: FocusOptions) {
		;(this.inputRef.value ?? this.pickerRef.value)?.focus(options)
		this.dispatchEvent(new Event('focus'))
	}

	public override click() {
		;(this.inputRef.value ?? this.pickerRef.value)?.click()
		this.dispatchEvent(new Event('click'))
	}

	public override blur() {
		;(this.inputRef.value ?? this.pickerRef.value)?.blur()
		this.dispatchEvent(new Event('blur'))
	}

	protected override render() {
		// The size-ladder's inline style still renders xxs/xs/sm/md below 16px
		// here — input.scss floors the *computed* font-size back to 16px on iOS
		// (`!important` inside the `-webkit-touch-callout` block) so focus never
		// zooms; the smaller ladder sizes apply on non-iOS only.
		const compact = this.density === 'compact' && !this.picker
		const spec = CONTROL_SIZE[compact ? 'xxs' : this.size]
		const styles: Record<string, string> = compact ? {} : controlSizeStyles(this.size)

		const showClear = this.clearable && !this.picker && !this.disabled && !this.readonly && this.value !== ''
		// The field's own trailing furniture — the picker's chevron, the clear
		// control — owns the trailing edge; an authored `suffix` is content and
		// yields, moving inboard by exactly this width. Zero with neither, which
		// is what keeps an ordinary field's box the box it has always had.
		const trailingFurniture = this.picker
			? spec.icon.input + spec.gap
			: showClear
				? CONTROL_SIZE[CLEAR_CONTROL_SIZE[this.size]].height + spec.gap
				: 0
		// An `actions` control is outermost in the trailing band and everything
		// else there steps inboard by this much. Measured rather than assumed,
		// because the control's width is the consumer's — a glyph when the field
		// is narrow, a glyph plus a label when it is not. Zero with an empty slot,
		// so a field that never uses the slot renders the box it always rendered.
		const actionsReserve = this.fieldActions.width > 0 ? this.fieldActions.width + spec.gap : 0

		// An adornment stands one field-padding in from the edge and occupies a
		// glyph plus the ladder's gap, so the text clears it. Written only when a
		// slot is actually filled: with neither, `styles` stays the object
		// `controlSizeStyles` returned, so an un-slotted field's box — and the
		// corner painted on it — is untouched by this whole feature.
		if (this.hasPrefix) styles.paddingInlineStart = `${spec.pad.input + spec.icon.input + spec.gap}px`
		if (this.hasSuffix || trailingFurniture || actionsReserve)
			styles.paddingInlineEnd = `${spec.pad.input + trailingFurniture + actionsReserve + (this.hasSuffix ? spec.icon.input + spec.gap : 0)}px`

		// The adornment box spans the input's own border box (`inset-y-0`), so the
		// content sits inside the field, optically centred on its text, and stays
		// click-through — the <input> lies under every pixel of it, so a tap on the
		// glyph lands on the field and focuses it with no handler.
		// Text adornments (a currency mark) take the field's own label scale;
		// `--schmancy-input-adornment-size` sizes a slotted <schmancy-icon>, whose
		// own `:host { font-size: 24px }` ignores inheritance (see input.scss).
		const adornmentStyles: Record<string, string> = {
			paddingInline: `${spec.pad.input}px`,
			fontSize: `${spec.labelFont}px`,
			'--schmancy-input-adornment-size': `${spec.icon.input}px`,
		}

		const isDateType = ['date', 'datetime-local', 'time', 'month', 'week'].includes(this.type)

		// A bare host aria-label (intercepted above) takes precedence over the
		// label/placeholder derivation and never triggers a visible <label> —
		// that stays keyed off `this.label` alone (OTP digit boxes want a name,
		// not a visible per-digit label). A host that carries its own role is
		// the named node itself (HostAriaLabelMixin keeps the name there), so
		// nothing is forwarded and the tree holds the name once.
		const effectiveAriaLabel = hostIsTheControl(this)
			? undefined
			: this.ariaLabel || this.label || this.placeholder || undefined

		// A picker row announces what it is AND what it currently holds, in that
		// order, because the value is the reason the row exists and a name of
		// "Event date" alone leaves a screen-reader user pressing to find out.
		// A visible <label> would otherwise win the name outright and hide the
		// value, so this is set unconditionally in picker mode rather than only
		// when nothing else names the button.
		const pickerAriaLabel =
			this.ariaLabel || [this.label, this.value || this.placeholder].filter(Boolean).join(', ') || undefined

		const inputClasses = {
			...fieldChromeClasses(this.error, false, this.variant),
			'appearance-auto cursor-pointer': this.type === 'color',
			'appearance-none': this.type !== 'color',
			// date inputs stay left-aligned regardless of `align`
			'caret-transparent focus:outline-hidden cursor-pointer select-none': this.readonly,
			'cursor-pointer': this.clickable,
			'text-left': this.align === 'left' || isDateType,
			'text-center': this.align === 'center' && !isDateType,
			'text-right': this.align === 'right' && !isDateType,
			autofilled: this.isAutofilled,
		}

		// The picker button wears the field's own chrome, so the two read as one
		// control family — but it brings its own focus ring, because neither half
		// of the chrome's reaches a <button>: `input.scss`'s `input:focus`
		// box-shadow is an element-selector rule for `input` alone, and the
		// chrome's `focus:outline-1` sets a width with no `outline-style`, which
		// resolves to `none` and paints nothing at any width. `outline-solid` is
		// the utility that actually emits the style — measured, never inferred
		// from the class string.
		const pickerClasses = {
			...fieldChromeClasses(this.error, false, this.variant),
			'text-left cursor-pointer': true,
			'text-muted': !this.value,
			// Hover reads on the field's own fill — the dominant surface — rather
			// than on the chevron, which is a fraction of the row's area. A filled
			// field's chrome already carries its own hover tint.
			'hover:bg-surface-containerLow': !this.disabled && this.variant === 'outlined',
			'transition-colors duration-150': !this.disabled,
			'focus:outline-solid focus:outline-2 focus:outline-offset-0': true,
			'focus:outline-secondary-default': !this.error,
			'focus:outline-error-default': this.error,
		}

		// Static literals (not template-composed) so the Tailwind scanner sees them.
		const labelClasses = {
			'block mb-1 font-medium': true,
			'opacity-40': this.disabled,
			'text-xxs': this.size === 'xxs',
			'text-xs': this.size === 'xs',
			'text-sm': this.size === 'sm' || this.size === 'md',
			'text-base': this.size === 'lg',
			// M3's resting label is `on-surface-variant`; `primary` is the focused state,
			// and spending it at rest collapses the two into one loud word.
			'text-surface-onVariant': !this.error,
			'text-error-default': this.error,
		}

		return html`
			<div class="w-full min-w-0 ${isDateType ? 'date-input-container' : ''}">
				${when(
					this.label,
					// `for` is dropped on a role-carrying host: the host is the named
					// node there (HostAriaLabelMixin), and a label that also named
					// the internal <input> would put the same string on two nodes of the
					// tree. The label stays visible and its clicks still reach whatever
					// handler the host bound.
					() =>
						html` <label
							for=${ifDefined(hostIsTheControl(this) ? undefined : this.id)}
							class=${this.classMap(labelClasses)}
						>
							${this.label}
						</label>`,
				)}

				<div class="relative w-full min-w-0">
					${when(
						this.picker,
						// A native <button> rather than an <input role="button">:
						// `button` is not an allowed role for a text input, and the
						// native element already carries the role, the tab stop, and
						// Enter/Space activation that a picker row is defined by.
						() =>
							html`<button
								${ref(this.pickerRef)}
								type="button"
								part="picker"
								id=${this.id}
								class=${this.classMap(pickerClasses)}
								style=${this.styleMap(styles)}
								?disabled=${this.disabled}
								aria-invalid=${this.error ? 'true' : 'false'}
								aria-describedby=${this.hint || this.validationMessage ? `hint-${this.id}` : nothing}
								aria-label=${ifDefined(pickerAriaLabel)}
							>
								${this.value || this.placeholder}
							</button>`,
						() =>
							html`<input
								${ref(this.inputRef)}
								id=${this.id}
								name=${this.name}
								class=${this.classMap(inputClasses)}
								style=${this.styleMap(styles)}
								.value=${this.value}
								.type=${this.otp ? 'text' : this.type}
								.autocomplete=${this.otp ? 'one-time-code' : this.autocomplete}
								.spellcheck=${this.spellcheck}
								placeholder=${this.placeholder}
								inputmode=${ifDefined(this.otp ? 'numeric' : this.inputmode)}
								pattern=${ifDefined(this.pattern)}
								.step=${this.step ?? ''}
								minlength=${ifDefined(this.minlength)}
								maxlength=${ifDefined(this.otp ? undefined : this.maxlength)}
								min=${ifDefined(this.min)}
								max=${ifDefined(this.max)}
								list=${ifDefined(this.list)}
								?required=${this.required}
								?disabled=${this.disabled}
								?readonly=${this.readonly}
								aria-invalid=${this.error ? 'true' : 'false'}
								aria-required=${this.required ? 'true' : 'false'}
								aria-describedby=${this.hint || this.validationMessage ? `hint-${this.id}` : nothing}
								aria-label=${ifDefined(effectiveAriaLabel)}
								${this.otp ? otp({ length: this.maxlength ?? 6, alignment: this.otpAlignment }) : nothing}
							/>`,
					)}

					<slot
						name="prefix"
						class="pointer-events-none absolute inset-y-0 start-0 flex items-center"
						style=${this.styleMap(adornmentStyles)}
						@slotchange=${this.syncAdornments}
					></slot>
					<slot
						name="suffix"
						class="pointer-events-none absolute inset-y-0 end-0 flex items-center"
						style=${this.styleMap(
							trailingFurniture || actionsReserve
								? {
										...adornmentStyles,
										paddingInlineEnd: `${spec.pad.input + trailingFurniture + actionsReserve}px`,
									}
								: adornmentStyles,
						)}
						@slotchange=${this.syncAdornments}
					></slot>

					${when(
						showClear,
						// pointer-events are restored explicitly: the two adornment slots
						// above turn them off across the whole overlay band, and this one
						// is a control that has to receive its own clicks.
						() =>
							html`<schmancy-icon-button
								part="clear"
								icon="close"
								color="neutral"
								size=${CLEAR_CONTROL_SIZE[this.size]}
								aria-label=${this.clearLabel || (this.label ? `Clear ${this.label}` : 'Clear')}
								class="pointer-events-auto absolute inset-y-0 end-0 my-auto"
								style=${this.styleMap({ marginInlineEnd: `${spec.pad.input + actionsReserve}px` })}
								@click=${this.clearValue}
							></schmancy-icon-button>`,
					)}
					${when(
						this.picker,
						// <schmancy-icon> stamps its own aria-hidden, so the chevron is a
						// mark for the eye alone and the button's name stays the name.
						() =>
							html`<div
								class="pointer-events-none absolute inset-y-0 end-0 flex items-center"
								style=${this.styleMap({
									paddingInlineStart: `${spec.pad.input}px`,
									paddingInlineEnd: `${spec.pad.input + actionsReserve}px`,
								})}
							>
								<schmancy-icon style=${this.styleMap({ fontSize: `${spec.icon.input}px` })}
									>chevron_right</schmancy-icon
								>
							</div>`,
					)}

					<!--
						Last in DOM order, so Tab leaves the field, passes the clear control,
						and lands here — the same order the eye reads across the trailing
						band. pointer-events-auto restores what the adornment slots turn off
						across this band; the control has to receive its own clicks, and the
						field's text padding above keeps them out of reach of a click aimed
						at the value. The box carries no padding of its own — the trailing
						inset positions it instead, so an empty slot measures zero and
						reserves nothing.
					-->
					<div
						${ref(this.fieldActions.observe)}
						part="actions"
						class="pointer-events-auto absolute inset-y-0 flex items-center"
						style=${this.styleMap({ insetInlineEnd: `${spec.pad.input}px`, gap: `${spec.gap}px` })}
					>
						<slot name="actions" @slotchange=${this.fieldActions.sync}></slot>
					</div>
				</div>

				${when(
					this.hint || (this.error && this.validationMessage),
					() => html`
						<div
							id="hint-${this.id}"
							class=${this.classMap({ 'mt-1 text-sm wrap-anywhere': true, 'text-error-default': this.error, 'text-surface-onVariant': !this.error })}
							role=${ifDefined(this.error ? 'alert' : undefined)}
						>
							${this.error && this.validationMessage ? this.validationMessage : this.hint}
						</div>
					`,
				)}
			</div>
		`
	}
}

/** Legacy tag alias. */
@customElement('sch-input')
export class SchmancyInputCompat extends SchmancyInput {}
